import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    DialogContentText,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { makeFormStyles } from 'forms/styles';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';

const DuplicateNodeDialog = ({ open, onClose, onConfirm, nodeCount }) => {
    const theme = useSelector(selectDefaultTheme);
    const formClasses = makeFormStyles(theme);
    const [duplicateCount, setDuplicateCount] = useState(1);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const value = e.target.value;

        // Strip anything that isn't a digit — blocks decimal points, minus signs,
        // plus signs, and any other non-integer characters from paste or input.
        const digitsOnly = value.replace(/\D/g, '');

        if (digitsOnly === '') {
            setDuplicateCount('');
            setError('');
            return;
        }

        const numValue = parseInt(digitsOnly, 10);

        if (numValue < 1) {
            setError('Please enter a valid number (minimum 1)');
        } else if (numValue > 100) {
            setError('Maximum 100 duplicates allowed');
        } else {
            setError('');
        }

        setDuplicateCount(digitsOnly);
    };

    const handleConfirm = () => {
        const numValue = parseInt(duplicateCount, 10);

        if (isNaN(numValue) || numValue < 1 || numValue > 100) {
            setError('Please enter a valid number between 1 and 100');
            return;
        }

        onConfirm(numValue);
        handleClose();
    };

    const handleClose = () => {
        setDuplicateCount(1);
        setError('');
        onClose();
    };

    const handleKeyDown = (e) => {
        if (['.', '-', '+', 'e', 'E'].includes(e.key)) {
            e.preventDefault();
            return;
        }
        if (e.key === 'Enter' && !error && duplicateCount) {
            e.preventDefault();
            handleConfirm();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Duplicate Node{nodeCount > 1 ? 's' : ''}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ marginBottom: 2 }}>
                    How many duplicates would you like to create for {nodeCount === 1 ? 'this node' : `these ${nodeCount} nodes`}?
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Number of Duplicates"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={duplicateCount}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    error={!!error}
                    helperText={error || 'Enter a number between 1 and 100'}
                    inputProps={{
                        min: 1,
                        max: 100,
                        step: 1,
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} className={formClasses.cancel}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    className={formClasses.submit}
                    disabled={!!error || !duplicateCount}
                >
                    Duplicate
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DuplicateNodeDialog;
